"use client"

import * as React from "react"

import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import {
  persistSidebarWidth,
  previewSidebarDrag,
  readStoredSidebarWidth,
  resolveSidebarDrag,
  resolveSidebarDragRelease,
  SIDEBAR_WIDTH_DEFAULT_PX,
  SIDEBAR_WIDTH_ICON_PX,
  SIDEBAR_WIDTH_MIN_PX,
  SIDEBAR_WIDTH_SNAP_DURATION_MS,
} from "@/lib/sidebar-width"
import {
  TooltipProvider,
} from "@/components/ui/tooltip"

/** Mobile sheet width; desktop expanded width comes from `--sidebar-width`. */
export const SIDEBAR_WIDTH_MOBILE = "min(22rem, calc(100vw - 4.25rem))"
export const SIDEBAR_WIDTH_ICON = "3rem"

const SIDEBAR_COOKIE_NAME = "sidebar_state"
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
const SIDEBAR_KEYBOARD_SHORTCUT = "b"

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

export type SidebarContextProps = {
  state: "expanded" | "collapsed"
  open: boolean
  setOpen: (open: boolean) => void
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  isMobile: boolean
  toggleSidebar: () => void
  /** Last expanded width in px (170–340). */
  width: number
  isResizing: boolean
  /** Icon chrome while dragging below the labelled min (still expanded). */
  dragCompact: boolean
  /** Desktop icon rail: collapsed or mid-drag below label min. Mobile drawer keeps labels. */
  effectiveCollapsed: boolean
  beginResize: () => void
  previewDragWidth: (pointerWidth: number) => void
  finishResize: (pointerWidth: number) => void
  applyPointerWidth: (pointerWidth: number) => void
  resetWidth: () => void
  persistWidth: () => void
}

const SidebarContext = React.createContext<SidebarContextProps | null>(null)

function useSidebar() {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.")
  }

  return context
}

const SidebarProvider = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    defaultOpen?: boolean
    open?: boolean
    onOpenChange?: (open: boolean) => void
    disableKeyboardShortcut?: boolean
  }
>(
  (
    {
      defaultOpen = true,
      open: openProp,
      onOpenChange: setOpenProp,
      disableKeyboardShortcut = false,
      className,
      style,
      children,
      ...props
    },
    ref
  ) => {
    const isMobile = useIsMobile()
    const [openMobile, setOpenMobile] = React.useState(false)
    const [width, setWidth] = React.useState(readStoredSidebarWidth)
    const [isResizing, setIsResizing] = React.useState(false)
    const [dragCompact, setDragCompact] = React.useState(false)
    const openRef = React.useRef(false)
    const widthRef = React.useRef(width)
    const lastExpandedWidthRef = React.useRef(width)
    const dragOpenRef = React.useRef(false)
    const dragCompactRef = React.useRef(false)
    const resizingRef = React.useRef(false)
    const settlingRef = React.useRef(false)
    const wrapperRef = React.useRef<HTMLDivElement | null>(null)
    const settleTimersRef = React.useRef<{ raf1: number; raf2: number; timeout: number }>({
      raf1: 0,
      raf2: 0,
      timeout: 0,
    })

    const [_open, _setOpen] = React.useState(defaultOpen)
    const open = openProp ?? _open
    openRef.current = open
    if (!resizingRef.current) {
      widthRef.current = width
    }
    const setOpen = React.useCallback(
      (value: boolean | ((value: boolean) => boolean)) => {
        const openState = typeof value === "function" ? value(open) : value
        if (setOpenProp) {
          setOpenProp(openState)
        } else {
          _setOpen(openState)
        }

        document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
      },
      [setOpenProp, open]
    )

    const toggleSidebar = React.useCallback(() => {
      return isMobile ? setOpenMobile((openToggle) => !openToggle) : setOpen((o) => !o)
    }, [isMobile, setOpen, setOpenMobile])

    const writeWidthVar = React.useCallback((px: number) => {
      wrapperRef.current?.style.setProperty("--sidebar-width", `${px}px`)
    }, [])

    const clearSettleTimers = React.useCallback(() => {
      const timers = settleTimersRef.current
      if (timers.raf1) cancelAnimationFrame(timers.raf1)
      if (timers.raf2) cancelAnimationFrame(timers.raf2)
      if (timers.timeout) window.clearTimeout(timers.timeout)
      settleTimersRef.current = { raf1: 0, raf2: 0, timeout: 0 }
    }, [])

    const cancelWidthSettle = React.useCallback(() => {
      clearSettleTimers()
      settlingRef.current = false
    }, [clearSettleTimers])

    const runAfterWidthPaint = React.useCallback((fn: () => void) => {
      clearSettleTimers()
      const timers = settleTimersRef.current
      timers.raf1 = requestAnimationFrame(() => {
        timers.raf2 = requestAnimationFrame(() => {
          timers.raf1 = 0
          timers.raf2 = 0
          fn()
        })
      })
    }, [clearSettleTimers])

    const setCompact = React.useCallback((next: boolean) => {
      if (dragCompactRef.current === next) return
      dragCompactRef.current = next
      setDragCompact(next)
    }, [])

    const setWrapperRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        wrapperRef.current = node
        if (typeof ref === "function") ref(node)
        else if (ref) ref.current = node
      },
      [ref]
    )

    React.useLayoutEffect(() => {
      if (isResizing || settlingRef.current) return
      writeWidthVar(width)
    }, [width, isResizing, open, writeWidthVar])

    React.useEffect(() => () => cancelWidthSettle(), [cancelWidthSettle])

    const beginResize = React.useCallback(() => {
      if (isMobile) return
      cancelWidthSettle()
      dragOpenRef.current = openRef.current
      if (!openRef.current) {
        writeWidthVar(SIDEBAR_WIDTH_ICON_PX)
        setCompact(true)
      }
      resizingRef.current = true
      wrapperRef.current?.setAttribute("data-sidebar-resizing", "true")
      setIsResizing(true)
    }, [cancelWidthSettle, isMobile, setCompact, writeWidthVar])

    const previewDragWidth = React.useCallback(
      (pointerWidth: number) => {
        if (isMobile || !resizingRef.current) return
        const result = previewSidebarDrag(pointerWidth, dragOpenRef.current)
        if (!result.open) return
        if (!dragOpenRef.current) {
          dragOpenRef.current = true
          if (!openRef.current) setOpen(true)
        }
        if (result.width >= SIDEBAR_WIDTH_MIN_PX) {
          lastExpandedWidthRef.current = result.width
          widthRef.current = result.width
        }
        setCompact(result.width < SIDEBAR_WIDTH_MIN_PX)
        writeWidthVar(result.width)
      },
      [isMobile, setCompact, setOpen, writeWidthVar]
    )

    const finishResize = React.useCallback(
      (pointerWidth: number) => {
        if (!resizingRef.current) return
        resizingRef.current = false
        wrapperRef.current?.removeAttribute("data-sidebar-resizing")
        setIsResizing(false)
        if (isMobile) return
        const result = resolveSidebarDragRelease(pointerWidth)
        if (result.open) {
          persistSidebarWidth(result.width)
          setWidth(result.width)
          widthRef.current = result.width
          lastExpandedWidthRef.current = result.width
          if (!openRef.current) setOpen(true)
          const showLabels = () => setCompact(false)
          if (pointerWidth < SIDEBAR_WIDTH_MIN_PX && !prefersReducedMotion()) {
            settlingRef.current = true
            runAfterWidthPaint(() => {
              writeWidthVar(result.width)
              settleTimersRef.current.timeout = window.setTimeout(() => {
                settleTimersRef.current.timeout = 0
                settlingRef.current = false
                showLabels()
              }, SIDEBAR_WIDTH_SNAP_DURATION_MS)
            })
          } else {
            writeWidthVar(result.width)
            showLabels()
          }
        } else {
          persistSidebarWidth(lastExpandedWidthRef.current)
          setWidth(lastExpandedWidthRef.current)
          widthRef.current = lastExpandedWidthRef.current
          setCompact(true)
          const commitCollapsed = () => {
            settlingRef.current = false
            if (openRef.current) setOpen(false)
            setCompact(false)
          }
          if (prefersReducedMotion()) {
            writeWidthVar(SIDEBAR_WIDTH_ICON_PX)
            commitCollapsed()
            return
          }
          settlingRef.current = true
          runAfterWidthPaint(() => {
            writeWidthVar(SIDEBAR_WIDTH_ICON_PX)
            settleTimersRef.current.timeout = window.setTimeout(() => {
              settleTimersRef.current.timeout = 0
              commitCollapsed()
            }, SIDEBAR_WIDTH_SNAP_DURATION_MS)
          })
        }
      },
      [isMobile, runAfterWidthPaint, setCompact, setOpen, writeWidthVar]
    )

    const applyPointerWidth = React.useCallback(
      (pointerWidth: number) => {
        if (isMobile) return
        cancelWidthSettle()
        setCompact(false)
        const result = resolveSidebarDrag(pointerWidth)
        if (result.open) {
          setWidth(result.width)
          widthRef.current = result.width
          writeWidthVar(result.width)
          if (!openRef.current) setOpen(true)
        } else if (openRef.current) {
          persistSidebarWidth(widthRef.current)
          setOpen(false)
        }
      },
      [cancelWidthSettle, isMobile, setCompact, setOpen, writeWidthVar]
    )

    const persistWidth = React.useCallback(() => {
      persistSidebarWidth(widthRef.current)
    }, [])

    const resetWidth = React.useCallback(() => {
      cancelWidthSettle()
      setCompact(false)
      setWidth(SIDEBAR_WIDTH_DEFAULT_PX)
      widthRef.current = SIDEBAR_WIDTH_DEFAULT_PX
      writeWidthVar(SIDEBAR_WIDTH_DEFAULT_PX)
      persistSidebarWidth(SIDEBAR_WIDTH_DEFAULT_PX)
      if (!openRef.current) setOpen(true)
    }, [cancelWidthSettle, setCompact, setOpen, writeWidthVar])

    React.useEffect(() => {
      if (!isResizing) return
      const previousCursor = document.body.style.cursor
      const previousSelect = document.body.style.userSelect
      document.body.style.cursor = "col-resize"
      document.body.style.userSelect = "none"
      return () => {
        document.body.style.cursor = previousCursor
        document.body.style.userSelect = previousSelect
      }
    }, [isResizing])

    React.useEffect(() => {
      if (disableKeyboardShortcut) return
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
          event.preventDefault()
          toggleSidebar()
        }
      }

      window.addEventListener("keydown", handleKeyDown)
      return () => window.removeEventListener("keydown", handleKeyDown)
    }, [toggleSidebar, disableKeyboardShortcut])

    const state = open ? "expanded" : "collapsed"
    const effectiveCollapsed = (state === "collapsed" || dragCompact) && !isMobile

    const contextValue = React.useMemo<SidebarContextProps>(
      () => ({
        state,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
        width,
        isResizing,
        dragCompact,
        effectiveCollapsed,
        beginResize,
        previewDragWidth,
        finishResize,
        applyPointerWidth,
        resetWidth,
        persistWidth,
      }),
      [
        state,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
        width,
        isResizing,
        dragCompact,
        effectiveCollapsed,
        beginResize,
        previewDragWidth,
        finishResize,
        applyPointerWidth,
        resetWidth,
        persistWidth,
      ]
    )

    return (
      <SidebarContext.Provider value={contextValue}>
        <TooltipProvider delayDuration={0}>
          <div
            style={
              {
                "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
                ...style,
              } as React.CSSProperties
            }
            data-sidebar-resizing={isResizing ? "true" : undefined}
            className={cn(
              "group/sidebar-wrapper flex h-full min-h-0 w-full min-w-0 has-[[data-variant=inset]]:bg-sidebar",
              className
            )}
            ref={setWrapperRef}
            {...props}
          >
            {children}
          </div>
        </TooltipProvider>
      </SidebarContext.Provider>
    )
  }
)
SidebarProvider.displayName = "SidebarProvider"

export { SidebarProvider, useSidebar }
